import { useState, useMemo } from 'react';
import { Plus, Edit3, Trash2, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Globe, Search, Tag } from 'lucide-react';
import { useCatalogStore, genId } from '../store/catalogStore';
import { toast } from '../components/Toast';
import type { Brand } from '../types/catalog';

const emptyBrand = (): Omit<Brand, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: '',
  slug: '',
  description: '',
  country: '',
  logo: '',
  isActive: true,
  displayOrder: 1,
});

export default function BrandsPage() {
  const { brands, addBrand, updateBrand, deleteBrand, reorderBrand } = useCatalogStore();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyBrand());
  const [logoPreview, setLogoPreview] = useState<string>('');

  const filtered = useMemo(
    () =>
      brands
        .filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [brands, search]
  );

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyBrand(), displayOrder: brands.length + 1 });
    setLogoPreview('');
    setShowForm(true);
  };

  const openEdit = (brand: Brand) => {
    setEditingId(brand.id);
    setForm({
      name: brand.name,
      slug: brand.slug,
      description: brand.description ?? '',
      country: brand.country ?? '',
      logo: brand.logo ?? '',
      isActive: brand.isActive,
      displayOrder: brand.displayOrder,
    });
    setLogoPreview(brand.logo ?? '');
    setShowForm(true);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      setLogoPreview(url);
      setForm((f) => ({ ...f, logo: url }));
    };
    reader.readAsDataURL(file);
  };

  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleSave = () => {
    if (!form.name.trim()) { toast.error('Validation', 'Brand name is required.'); return; }
    const slug = form.slug.trim() || slugify(form.name);
    const now = new Date().toISOString();
    if (editingId) {
      updateBrand(editingId, { ...form, slug });
      toast.success('Brand Updated', `"${form.name}" has been updated.`);
    } else {
      addBrand({ id: genId(), ...form, slug, createdAt: now, updatedAt: now });
      toast.success('Brand Added', `"${form.name}" has been added.`);
    }
    setShowForm(false);
  };

  const handleDelete = (brand: Brand) => {
    if (!confirm(`Delete brand "${brand.name}"? This cannot be undone.`)) return;
    deleteBrand(brand.id);
    toast.success('Brand Deleted', `"${brand.name}" removed.`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Brands</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{brands.length} brands configured</p>
        </div>
        <button
          onClick={openAdd}
          className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Brand
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brands..."
          className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
        />
      </div>

      {/* Brand Table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-4 py-3 text-left font-medium text-gray-500">Order</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Logo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Brand Name</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Country</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Description</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  <Tag className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No brands found</p>
                </td>
              </tr>
            )}
            {filtered.map((brand) => (
              <tr key={brand.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => reorderBrand(brand.id, 'up')}
                      className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center"
                      aria-label="Move up"
                    >
                      <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    <button
                      onClick={() => reorderBrand(brand.id, 'down')}
                      className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center"
                      aria-label="Move down"
                    >
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                    <span className="text-gray-400 self-center ml-1">{brand.displayOrder}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {brand.logo ? (
                    <img src={brand.logo} alt={brand.name} className="w-10 h-10 object-contain rounded-lg border border-gray-100" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center text-[10px] font-bold text-gray-400 uppercase">
                      {brand.name.slice(0, 2)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{brand.name}</p>
                  <p className="text-gray-400 text-[11px]">{brand.slug}</p>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  {brand.country && (
                    <span className="flex items-center gap-1 text-gray-500">
                      <Globe className="w-3.5 h-3.5" /> {brand.country}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <p className="text-gray-500 truncate max-w-[200px]">{brand.description}</p>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => updateBrand(brand.id, { isActive: !brand.isActive })}
                    aria-label={brand.isActive ? 'Disable' : 'Enable'}
                  >
                    {brand.isActive ? (
                      <span className="flex items-center gap-1.5 text-emerald-600">
                        <ToggleRight className="w-5 h-5" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-gray-400">
                        <ToggleLeft className="w-5 h-5" /> Inactive
                      </span>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => openEdit(brand)}
                      className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                      aria-label="Edit brand"
                    >
                      <Edit3 className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(brand)}
                      className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center"
                      aria-label="Delete brand"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-[17px] font-semibold text-gray-900">
                {editingId ? 'Edit Brand' : 'Add Brand'}
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Logo */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-2">Brand Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain rounded-xl border border-gray-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-300 text-[10px]">Logo</div>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="h-9 px-4 rounded-xl bg-gray-50 border border-gray-200 text-[13px] text-gray-600 font-medium cursor-pointer hover:bg-gray-100 flex items-center gap-2">
                      Upload Image
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    </label>
                    <input
                      value={form.logo}
                      onChange={(e) => { setForm((f) => ({ ...f, logo: e.target.value })); setLogoPreview(e.target.value); }}
                      placeholder="Or paste image URL..."
                      className="h-9 px-3 rounded-xl border border-gray-200 text-[12px] w-full focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                    />
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Brand Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: slugify(e.target.value) }))}
                  placeholder="e.g. Dahua"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Slug</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="e.g. dahua"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Country of Origin</label>
                <input
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  placeholder="e.g. China"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[12px] font-medium text-gray-500 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  placeholder="Short brand description..."
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                />
              </div>

              {/* Display Order & Active */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-[12px] font-medium text-gray-500 mb-1">Display Order</label>
                  <input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) => setForm((f) => ({ ...f, displayOrder: +e.target.value }))}
                    className="w-full h-10 px-3 rounded-xl border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5"
                  />
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      className="w-4 h-4 accent-[#7FA86A]"
                    />
                    <span className="text-[13px] text-gray-600">Active</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="h-10 px-5 rounded-xl bg-[#7FA86A] text-white text-[13px] font-medium hover:bg-[#658C53]"
              >
                {editingId ? 'Save Changes' : 'Add Brand'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
